import type {
  Contact,
  ContactStatusFilterValue,
  ContactsCountByCountry,
  PrivateData,
  RevealBulkResponse,
  ContactDetailsFilterDto,
} from '../../domain/contact';
import type { Country } from '../../domain/country';
import type { Persona } from '../../domain/persona';
import { BASE_API_URL, HttpClient } from '../../services/HttpClient';
import type { RequireAtLeastOne } from '../../services/typeUtils';

type ContactDTO = Omit<Contact, 'matchedProfiles'> & { matchedProfiles?: string[] };

export type GetContactsParams = {
  linkedinId?: string;
  page?: number;
  perPage?: number;
  domain?: string;
  searchText?: string;
  persona?: Persona;
  includeCountries?: Country[];
  contactDetails: ContactDetailsFilterDto;
  status: ContactStatusFilterValue;
};

type GetContactsPayload = {
  searchText?: string;
  profiles: Persona[];
  employeeLocation?: { includeCountryCodes: number[] };
  contactDetails?: ContactDetailsFilterDto;
  includeOnlyNotRevealed?: boolean;
  includeOnlyRevealed?: boolean;
  isExportedToSalesforce?: boolean;
  isExportedToHubspot?: boolean;
};

export type LinkedinContactsPayload = {
  numericCompanyLinkedinIds: number[];
  fullName: string;
  companyNames: string[];
  linkedinId?: string;
  linkedinSalesNavId?: string;
};

export type CrmContactsPayload = RequireAtLeastOne<
  {
    FullName: string;
    Domain: string;
    CompanyName: string;
  },
  'Domain' | 'CompanyName'
>;

const applyContactStatusToPayload = (status: ContactStatusFilterValue, payload: GetContactsPayload) => {
  const updatedPayload = { ...payload };

  if (status.notRevealed && status.notExported && status.exported) {
    return updatedPayload;
  }

  if (status.notRevealed && status.exported && !status.notExported) {
    updatedPayload.includeOnlyNotRevealed = true;
    updatedPayload.isExportedToSalesforce = true;
    updatedPayload.isExportedToHubspot = true;
    return updatedPayload;
  }

  if (status.notRevealed) {
    updatedPayload.includeOnlyNotRevealed = true;
  }

  if (status.exported && status.notExported) {
    updatedPayload.includeOnlyRevealed = true;
  }

  if (status.exported && !status.notExported) {
    updatedPayload.isExportedToSalesforce = true;
    updatedPayload.isExportedToHubspot = true;
  } else if (status.notExported && !status.exported) {
    updatedPayload.includeOnlyRevealed = true;
    updatedPayload.isExportedToSalesforce = false;
    updatedPayload.isExportedToHubspot = false;
  }

  return updatedPayload;
};

const createContactRevealPayload = (contact: Contact) => ({
  contactId: contact.id,
  companyId: contact.companyId,
  prospect: contact.originalSite,
  hasValidEmails: contact.hasValidEmails,
  hasMobilePhoneNumbers: contact.hasMobilePhoneNumbers,
  hasDirectPhoneNumbers: contact.hasDirectPhoneNumbers,
  linkedInUrl: contact.linkedin,
});

const REVEAL_URL = '/sales-extension/api/contacts/reveal';

export const contactsApi = {
  getContacts: async ({
    linkedinId,
    domain,
    page = 0,
    perPage = 20,
    searchText = '',
    persona,
    includeCountries = [],
    contactDetails,
    status,
  }: GetContactsParams): Promise<Contact[]> => {
    const searchParams = new URLSearchParams();

    if (linkedinId) {
      searchParams.append('linkedinId', linkedinId);
    }
    if (domain) {
      searchParams.append('domain', domain);
    }
    searchParams.append('page', `${page}`);
    searchParams.append('pageSize', `${perPage}`);

    const url = `/sales-extension/api/contacts?${searchParams.toString()}`;
    let payload: GetContactsPayload = { searchText, profiles: [] };
    payload = applyContactStatusToPayload(status, payload);

    if (contactDetails) {
      payload.contactDetails = contactDetails;
    }

    if (includeCountries.length > 0) {
      payload.employeeLocation = { includeCountryCodes: includeCountries.map((c) => c.id) };
    }

    if (persona) {
      payload.profiles.push(persona);
    }

    const contactsDTO = await HttpClient.post<{ rows: ContactDTO[] } | undefined>(url, payload);

    const contacts = (contactsDTO?.rows || []).map((contact) => ({
      ...contact,
      matchedProfiles: contact.matchedProfiles || [],
    }));

    return contacts;
  },

  getContactsCountByCountries: async ({
    linkedinId,
    domain,
    searchText,
    persona,
    contactDetails,
    status,
  }: GetContactsParams): Promise<ContactsCountByCountry[]> => {
    const searchParams = new URLSearchParams();

    if (linkedinId) {
      searchParams.append('linkedinId', linkedinId);
    }
    if (domain) {
      searchParams.append('domain', domain);
    }

    let payload: GetContactsPayload = { searchText, profiles: [] };
    payload = applyContactStatusToPayload(status, payload);

    if (persona) {
      payload.profiles.push(persona);
    }

    if (contactDetails) {
      payload.contactDetails = contactDetails;
    }

    const url = `/sales-extension/api/contacts/count-by-countries?${searchParams.toString()}`;

    type ContactsCountByCountryDTO = { country: { id: number; name: string }; contactsCount: number };
    const response = await HttpClient.post<ContactsCountByCountryDTO[]>(url, payload);

    return response.map(({ country, contactsCount }) => ({
      country: { id: country.id, text: country.name },
      contactsCount,
    }));
  },

  getPrivateData: async (contact: Contact) => {
    const payload = createContactRevealPayload(contact);
    const response = await HttpClient.post<PrivateData>(REVEAL_URL, payload);
    return response;
  },

  getBulkPrivateData: async (contacts: Contact[]) => {
    const payload = contacts.map((contact) => createContactRevealPayload(contact));
    const url = `${REVEAL_URL}/bulk`;
    const response = await HttpClient.post<RevealBulkResponse>(url, { contacts: payload });
    return response;
  },

  getBulkRevealStatus: async (requestId: string): Promise<number> => {
    const url = `${REVEAL_URL}/bulk/status?requestId=${requestId}`;
    const response = await HttpClient.get<number>(url);
    return response;
  },

  getLinkedinContacts: async (
    searchContacts: LinkedinContactsPayload[],
    signal?: AbortSignal
  ): Promise<(Contact | null)[]> => {
    return await HttpClient.post<(Contact | null)[]>(
      `${BASE_API_URL}/linkedin/contacts`,
      { searchContacts },
      { signal }
    );
  },

  getContactsByEmail: async (emails: string[], signal?: AbortSignal): Promise<{ contacts: Contact[] }> =>
    HttpClient.post<{ contacts: Contact[] }>('/sales-extension/api/contacts/search/email', { emails }, { signal }),

  getCrmContacts: async (searchContacts: CrmContactsPayload[], signal?: AbortSignal): Promise<(Contact | null)[]> => {
    return await HttpClient.post<(Contact | null)[]>(
      `${BASE_API_URL}/crm/contacts`,
      { SearchContacts: searchContacts },
      { signal }
    );
  },
};
